import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { dateTime, useBoolean } from '@/_shared';

import type { EmailQuerySchema } from '../shared';
import { defaultValues, emailCreateFormSchema } from '../shared';

export type Props = {
  onChange: (v: EmailQuerySchema) => number;
};

export function useCreateEmail({ onChange }: Props) {
  const [isPending, setIsPending] = useBoolean();
  const form = useForm({
    resolver: zodResolver(emailCreateFormSchema),
    defaultValues: { ...defaultValues, id: dateTime.now().valueOf() },
  });
  const email = form.watch('email');
  // const { isPending, mutateAsync } = useCreateMutation();

  const validateForm = useCallback(async () => {
    const newV = form.getValues();
    const res = await form.trigger();
    if (!res) {
      // Return undefined to differentiate between API error and form validation error
      return undefined;
    }

    return newV;
  }, [form]);

  const onSubmit = useCallback(async () => {
    setIsPending.setTrue();
    const newV = await validateForm();
    if (newV) {
      const res = onChange({ ...newV });
      if (res === -2) {
        form.setError('email', { type: 'manual', message: 'Email already exists.' });
        setIsPending.setFalse();
        return false;
      }
    }
    setIsPending.setFalse();
    form.reset();
    form.setValue('id', dateTime.now().valueOf());
    return Boolean(newV);
  }, [form, onChange, setIsPending, validateForm]);

  useEffect(() => {
    if (email && form.formState.errors.email?.type === 'manual') {
      form.clearErrors();
    }
  }, [email, form]);

  return { id: form.getValues('id'), form, onSubmit, validateForm, isPending };
}

export type UseCreateEmail = ReturnType<typeof useCreateEmail>;
